/**
 * PostHog Analytics Utilities
 *
 * Provides wrapper functions for PostHog analytics and error tracking.
 * These functions safely handle cases where PostHog is not initialized.
 */

import posthog from 'posthog-js';
import { useFeatureFlagEnabled } from 'posthog-js/react';

// PostHog configuration
const POSTHOG_HOST_DIRECT = 'https://us.i.posthog.com';
const POSTHOG_HOST_PROXY = '/phs';
export const POSTHOG_API_KEY = 'phc_lU9R7OyaXldpBlRUpPg8ZP78nLbcQy1ucdAo7m3bXHs';

// Domains where PostHog should run
const ALLOWED_ROOTS = ['recruit41.com', 'learn41.com', 'devr41.com', 'devl41.com'];

const hostname = window.location.hostname;

// Check if hostname matches primary or subdomain (*.root.com)
const isAllowedDomain = ALLOWED_ROOTS.some(
  (root) => hostname === root || hostname.endsWith(`.${root}`),
);

// Only enable PostHog when in allowed domain
export const isPostHogEnabled = isAllowedDomain && Boolean(POSTHOG_API_KEY);

/**
 * Determine PostHog API host based on current domain.
 * Uses reverse proxy in deployed environments to bypass ad blockers.
 * Same pattern as Monaco Editor CDN proxy.
 *
 * Architecture:
 * - devr41.com, qar41.com, recruit41.com: /phs/* -> GCP Load Balancer -> us.i.posthog.com
 * - localr41.com: Direct to us.i.posthog.com (no local proxy)
 *
 * Note: Using '/phs' instead of '/ingest' because '/ingest' is a known PostHog
 * proxy path that's included in ad blocker filter lists.
 */
function getPostHogHost(): string {
  const parts = hostname.split('.');
  const baseDomain = parts.length >= 2 ? parts.slice(-2).join('.') : hostname;

  // Local development doesn't have the proxy in nginx
  if (baseDomain === 'localr41.com') {
    return POSTHOG_HOST_DIRECT;
  }

  // Deployed environments use the reverse proxy (bypasses ad blockers)
  return POSTHOG_HOST_PROXY;
}

const POSTHOG_HOST = getPostHogHost();

// Log which PostHog host is being used (helpful for debugging)
if (isPostHogEnabled && (hostname.includes('localr41.com') || hostname.includes('devr41.com'))) {
  console.log(`[PostHog] Loading from: ${POSTHOG_HOST}`);
}

// PostHog options - session recording disabled by default
export const posthogOptions = {
  api_host: POSTHOG_HOST,
  defaults: '2025-11-30',
  person_profiles: 'identified_only' as const, // Anonymous by default, identified after identify() call
  enable_heatmaps: false, // Enabled per-route via posthog.set_config() in candidate profile
  disable_session_recording: true, // Disabled by default, enabled only on meeting routes
  session_recording: {
    recordCrossOriginIframes: true,
    console_log_recording_enabled: true, // Capture full console logs
  },
} as const;

/**
 * Identify a user in PostHog with their properties
 * @param userId - Unique user identifier
 * @param userProperties - Additional user properties to track
 */
function identifyUser(
  userId: string | number,
  userProperties?: Record<string, any>,
): void {
  try {
    if (posthog && posthog.__loaded) {
      posthog.identify(String(userId), userProperties);
    }
  } catch (error) {
    console.error('Failed to identify user in PostHog:', error);
  }
}

/**
 * Reset the current user (e.g., on logout)
 * This clears the user identity and generates a new anonymous ID
 */
function resetUser(): void {
  try {
    if (posthog && posthog.__loaded) {
      posthog.reset();
    }
  } catch (error) {
    console.error('Failed to reset user in PostHog:', error);
  }
}

/**
 * Capture an exception in PostHog for error tracking
 * @param error - The error object or message to capture
 * @param context - Additional context about the error
 */
export function captureException(
  error: Error | string | unknown,
  context?: Record<string, any>,
): void {
  try {
    if (posthog && posthog.__loaded) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      const errorStack = error instanceof Error ? error.stack : undefined;

      posthog.captureException(error, {
        ...context,
        errorMessage,
        errorStack,
      });
    }
  } catch (captureError) {
    console.error('Failed to capture exception in PostHog:', captureError);
  }
}

/**
 * Capture a custom event in PostHog
 * @param eventName - Name of the event
 * @param properties - Event properties
 */
export function captureEvent(
  eventName: string,
  properties?: Record<string, any>,
): void {
  try {
    if (posthog && posthog.__loaded) {
      posthog.capture(eventName, properties);
    }
  } catch (error) {
    console.error('Failed to capture event in PostHog:', error);
  }
}

/**
 * Capture an event using sendBeacon transport for reliability during page unload.
 * Use this instead of captureEvent inside useEffect cleanup / beforeunload handlers
 * where the normal XHR transport may be cancelled by the browser.
 */
export function captureBeaconEvent(
  eventName: string,
  properties?: Record<string, any>,
): void {
  try {
    if (posthog && posthog.__loaded) {
      posthog.capture(eventName, properties, { transport: 'sendBeacon' });
    }
  } catch (error) {
    console.error('Failed to capture beacon event in PostHog:', error);
  }
}

/**
 * Set user properties that will be sent with all future events
 * @param properties - Properties to set for the user
 */
function setUserProperties(properties: Record<string, any>): void {
  try {
    if (posthog && posthog.__loaded) {
      posthog.people.set(properties);
    }
  } catch (error) {
    console.error('Failed to set user properties in PostHog:', error);
  }
}

/**
 * Start session recording in PostHog
 * Used when user enters meeting view routes
 */
export function startSessionRecording(): void {
  try {
    if (posthog && posthog.__loaded) {
      posthog.startSessionRecording();
    }
  } catch (error) {
    console.error('Failed to start session recording in PostHog:', error);
  }
}

/**
 * Stop session recording in PostHog
 * Used when user leaves meeting view routes
 */
export function stopSessionRecording(): void {
  try {
    if (posthog && posthog.__loaded) {
      posthog.stopSessionRecording();
    }
  } catch (error) {
    console.error('Failed to stop session recording in PostHog:', error);
  }
}

/**
 * Set tenant context as a super property in PostHog
 * This property will be sent with all subsequent events
 * @param tenantName - The tenant name to track
 */
export function setTenantContext(tenantName: string): void {
  try {
    if (posthog && posthog.__loaded) {
      posthog.register({
        tenant_name: tenantName,
      });
    }
  } catch (error) {
    console.error('Failed to set tenant context in PostHog:', error);
  }
}

/**
 * Set interview context as super properties in PostHog
 * These properties will be sent with all subsequent events
 * Allows filtering events by interview_id in PostHog dashboard
 * @param interviewId - The interview ID to track
 */
export function setInterviewContext(interviewId: string): void {
  try {
    if (posthog && posthog.__loaded) {
      posthog.register({
        interview_id: interviewId,
      });
    }
  } catch (error) {
    console.error('Failed to set interview context in PostHog:', error);
  }
}

/**
 * React hook to read a PostHog boolean feature flag.
 * Returns false while PostHog is still loading the flag definition,
 * so callers default to "flag off" during the initial render window.
 */
export function useFeatureFlag(flagName: string): boolean {
  const enabled = useFeatureFlagEnabled(flagName);
  return enabled === true;
}

/**
 * Force a fresh flag fetch from PostHog, then return the boolean value.
 * Use at decision points (e.g., Join button) where the hook's ~30s cache
 * would let a user slip through a kill-switch that was just flipped.
 * Falls back to the cached value if PostHog is unavailable or the reload
 * doesn't resolve within timeoutMs.
 */
export function reloadAndCheckFlag(
  flagName: string,
  timeoutMs = 2000,
): Promise<boolean> {
  return new Promise((resolve) => {
    if (!posthog || !posthog.__loaded) {
      resolve(false);
      return;
    }

    let settled = false;
    let unsubscribe: (() => void) | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const finish = (value: boolean) => {
      if (settled) return;
      settled = true;
      if (unsubscribe) unsubscribe();
      if (timeoutId) clearTimeout(timeoutId);
      resolve(value);
    };

    try {
      unsubscribe = posthog.onFeatureFlags(() => {
        finish(posthog.isFeatureEnabled(flagName) === true);
      });
      posthog.reloadFeatureFlags();
    } catch {
      finish(posthog.isFeatureEnabled(flagName) === true);
      return;
    }

    timeoutId = setTimeout(
      () => finish(posthog.isFeatureEnabled(flagName) === true),
      timeoutMs,
    );
  });
}

/**
 * Clear interview context from PostHog super properties
 * Used when user leaves interview routes
 */
export function clearInterviewContext(): void {
  try {
    if (posthog && posthog.__loaded) {
      posthog.unregister('interview_id');
    }
  } catch (error) {
    console.error('Failed to clear interview context in PostHog:', error);
  }
}
